import { useEffect, useState } from 'preact/hooks';
import { BackLink } from '../components/BackLink';
import { isStandaloneDisplay } from '../lib/displayMode';
import { audioPlayability, looksLikeAudio } from '../lib/audioSupport';
import { PlaceholderScreen } from './PlaceholderScreen';
import {
  getSource,
  fetchSourceOriginal,
  fetchSourcePlayable,
  sourceOriginalUrl,
  ApiError,
  type SourceDoc,
  type SourceOriginal,
} from '../lib/api';
import './SourceViewer.css';

export interface SourceViewerProps {
  tenantId: string;
  id: string;
  onBack: () => void;
}

type LoadState = 'loading' | 'ready' | 'error';

/** The engine's own heading for the lines nothing could place
 * (actorCapture's UNREADABLE_NOTE_HEADING). Matched rather than re-worded so
 * this screen can tell that block apart from a PACK's terms block — whose
 * wording is the pack's and is rendered exactly as declared. */
const UNREADABLE_HEADING = "couldn't read these lines — add by hand:";

const KIND_LABEL: Record<string, string> = {
  email: 'Email',
  call_sheet: 'Call sheet',
  callsheet: 'Call sheet',
  contract: 'Contract',
  deal_memo: 'Deal memo',
  sides: 'Sides',
  breakdown: 'Breakdown',
  script: 'Script',
  old_resume: 'Old resume',
};

function formatReceived(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// B6b (Huan 2026-07-13 correction): the source IS the ORIGINAL file. This
// viewer opens the retained call-sheet PDF/image in its original form (an
// <iframe> for a PDF, an <img> for an image), served by GET
// /api/actor/sources/:id/original. The extracted raw_text is DEMOTED to a
// clearly-secondary, collapsible "our reading" section BELOW the original —
// the file above is the source of truth. Reached via `.srclink` chips from the
// audition breakdown and the booked project's day-of card.
export function SourceViewer({ tenantId, id, onBack }: SourceViewerProps) {
  const [state, setState] = useState<LoadState>('loading');
  // CV5 §1 — the full-size image, IN THE APP. See the render for why this
  // replaced a top-level navigation rather than joining it.
  const [zoomed, setZoomed] = useState(false);
  const [source, setSource] = useState<SourceDoc | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The retained original (object URL + mime). Fetched only when the source
  // actually has a stored file (media_ref); null otherwise.
  const [original, setOriginal] = useState<SourceOriginal | null>(null);
  const [originalError, setOriginalError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let revoke: string | null = null;
    async function load() {
      setState('loading');
      setOriginal(null);
      setOriginalError(null);
      try {
        const data = await getSource(tenantId, id);
        if (cancelled) return;
        setSource(data);
        setState('ready');
        if (data.media_ref) {
          try {
            // CV7 — FETCH WHAT THIS DEVICE CAN DECODE, not always what was
            // stored. An Android or desktop recording arrives as webm/opus,
            // which no iPhone can play (audioSupport.ts's matrix), so the
            // server keeps an AAC rendition beside it. `/playable` serves the
            // rendition when there is one and the original when there is not,
            // so this screen never has to choose. Download stays on
            // `/original` below — the recording as it was spoken is still the
            // truth, and still what the tech hands to anyone who asks.
            const wantsRendition = data.playback_status === 'rendered';
            const orig = wantsRendition
              ? await fetchSourcePlayable(tenantId, id)
              : await fetchSourceOriginal(tenantId, id);
            if (cancelled) {
              URL.revokeObjectURL(orig.objectUrl);
              return;
            }
            revoke = orig.objectUrl;
            setOriginal(orig);
          } catch (err) {
            if (cancelled) return;
            setOriginalError(err instanceof ApiError ? err.message : 'Could not load the original file.');
          }
        }
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err instanceof ApiError ? err.message : 'Could not reach the server.');
        setState('error');
      }
    }
    void load();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [tenantId, id]);

  if (state === 'loading') {
    return (
      <div class="source-viewer">
        <BackLink label="back" onClick={onBack} />
        <PlaceholderScreen eyebrow="Source" line="One moment…" />
      </div>
    );
  }

  if (state === 'error' || !source) {
    return (
      <div class="source-viewer">
        <BackLink label="back" onClick={onBack} />
        <PlaceholderScreen eyebrow="Source" line="Couldn't load this source." soft={errorMessage ?? undefined} />
      </div>
    );
  }

  const kind = source.kind ? (KIND_LABEL[source.kind] ?? source.kind) : null;
  const filename = source.filename;
  const received = formatReceived(source.received_at);
  const title = filename ?? kind ?? 'Original source';
  const metaParts = [kind, filename && filename !== title ? filename : null, received].filter(Boolean);
  const rawText = source.raw_text;
  const hasOriginal = Boolean(source.media_ref);
  // ⚑ WHAT THIS DOCUMENT SAYS, AND WHAT NOTHING COULD READ — SEPARATELY, AND
  // ABOVE THE DUMP (2026-09-16, the Day 3–4 walk, item 1). Both have been
  // written onto the filed original since W4/P1 and neither had a section: the
  // walk opened "our reading" and found the per-scale line, the commission
  // line and the whole email in one undifferentiated block. A receipt rendered
  // as wallpaper is a receipt nobody reads.
  const noteBlocks = source.note_blocks ?? [];
  const headedBlocks = noteBlocks.filter((b) => b.heading !== null);
  const downloadName = filename ?? 'original';
  const isImage = original?.mimeType.startsWith('image/') ?? false;
  // CV5 §5 — a recording is not a document. Before this, `audio/webm` fell to
  // the <iframe> below and rendered as a large empty dark rectangle with no
  // controls: the tech's own words, on screen, unplayable and unexplained.
  // The served mime is the truth once the bytes are in hand; until then the
  // FILENAME is the only thing this screen knows, and the walk showed why it
  // matters — a voice note spent its whole load beat reading "the original
  // file, exactly as it was sent", which is not what a recording is.
  const isAudio = original ? original.mimeType.startsWith('audio/') : looksLikeAudio(filename);
  const playability = isAudio && original ? audioPlayability(original.mimeType) : 'unknown';
  const cannotPlay = isAudio && playability === 'unsupported';
  // CV5 §1 — "OPEN ORIGINAL" IS A TRAP IN AN INSTALLED APP, AND THIS IS THE
  // ONE THAT STRANDED HIM.
  //
  // CV4 repointed this link from a dead `blob:` URL at a real same-origin
  // route, which fixed the button and broke the way home: the manifest
  // declares `display: standalone`, so a same-origin `target="_blank"`
  // navigates THE APP'S OWN WINDOW — no toolbar, no back, no swipe-back. The
  // only escape was force-quitting Vowric. A dead button is a nuisance; a trap
  // is a defect of a different order.
  //
  // Two answers, and the first one removes the need for the link entirely:
  //   · an IMAGE is already inline directly above, full width, and its bytes
  //     are already in memory — so "full size" is an in-app overlay (`zoomed`)
  //     and never leaves the SPA. This is strictly better than the link even
  //     in a browser tab, so it is not conditional.
  //   · anything else (a PDF, an unplayable recording) genuinely benefits from
  //     the browser's own viewer, so the link SURVIVES — but only where a
  //     browser can bring the user back (`!isStandaloneDisplay()`).
  // DOWNLOAD is untouched, on the object URL, for every type: `<a download>`
  // is not a navigation, and it is the honest way out when the app itself
  // cannot render the bytes.
  const canLeaveTheApp = !isStandaloneDisplay();
  const openOriginalHref = `${sourceOriginalUrl(tenantId, id)}?tenant=${encodeURIComponent(tenantId)}`;
  // CV7: true when what the player is holding is the AAC copy rather than the
  // recording itself — the one case where Download must NOT reuse the bytes
  // already in memory.
  const playingRendition = source.playback_status === 'rendered';

  return (
    <div class="source-viewer">
      <BackLink label="back" onClick={onBack} />
      <div class="source-viewer__title">{title}</div>
      {metaParts.length > 0 && <div class="source-viewer__meta">{metaParts.join(' · ')}</div>}

      {hasOriginal ? (
        <>
          <div class="source-viewer__note">
            {isAudio
              ? 'The recording, exactly as it was spoken — this is the source of truth.'
              : 'The original file, exactly as it was sent — this is the source of truth.'}
          </div>
          {original ? (
            <>
              <div class="srcfile">
                {isImage ? (
                  <button
                    type="button"
                    class="srcfile__zoom"
                    data-testid="source-zoom-open"
                    onClick={() => setZoomed(true)}
                  >
                    <img class="srcfile__img" src={original.objectUrl} alt={downloadName} />
                  </button>
                ) : isAudio ? (
                  cannotPlay ? (
                    // NEVER A SILENT BLACK BOX. This device has no decoder for
                    // this container (the ordinary case: a `.webm` recorded on
                    // desktop Chromium, opened inside WKWebView). Say it, and
                    // hand over the file — the download below is the way to
                    // hear it.
                    <div class="srcaudio srcaudio--unsupported" data-testid="source-audio-unsupported">
                      <span class="srcaudio__mark" aria-hidden="true">
                        ♪
                      </span>
                      <span class="srcaudio__say">
                        This phone can’t play a {original.mimeType.split(';')[0]} recording. Download it to
                        listen, or open it on a computer — the recording itself is safe.
                      </span>
                    </div>
                  ) : (
                    // eslint-disable-next-line jsx-a11y/media-has-caption -- a
                    // spoken field note has no caption track; the label names it.
                    <audio
                      class="srcaudio__el"
                      data-testid="source-audio"
                      src={original.objectUrl}
                      controls
                      preload="metadata"
                      aria-label="Recorded note"
                    />
                  )
                ) : (
                  <iframe class="srcfile__frame" src={original.objectUrl} title={downloadName} />
                )}
              </div>
              <div class="srcfile__actions">
                {isImage ? (
                  <button
                    type="button"
                    class="srcfile__open"
                    data-testid="source-full-size"
                    onClick={() => setZoomed(true)}
                  >
                    Full size
                  </button>
                ) : canLeaveTheApp ? (
                  <a class="srcfile__open" href={openOriginalHref} target="_blank" rel="noopener noreferrer">
                    Open original
                  </a>
                ) : null}
                {/* CV7 — DOWNLOAD MUST HAND OVER THE RECORDING, NOT OUR COPY
                    OF IT. For everything else the object URL is the bytes
                    already in hand and costs no second fetch. But when the
                    player is holding an AAC RENDITION, that object URL is the
                    derivative — downloading it would quietly replace "the
                    recording, exactly as it was spoken" (CV5 §4) with a
                    transcode, and the label above would be a lie. So a
                    rendition sends Download to /original instead; a download
                    is not a navigation, so this is safe in a standalone
                    window (CV5 §1 concerned target="_blank"). */}
                <a
                  class="srcfile__download"
                  href={playingRendition ? openOriginalHref : original.objectUrl}
                  download={downloadName}
                >
                  Download
                </a>
              </div>
              {zoomed && isImage ? (
                <div
                  class="srczoom"
                  data-testid="source-zoom"
                  role="dialog"
                  aria-modal="true"
                  aria-label={downloadName}
                  onClick={() => setZoomed(false)}
                >
                  <img class="srczoom__img" src={original.objectUrl} alt={downloadName} />
                  <button type="button" class="srczoom__close" data-testid="source-zoom-close">
                    Close
                  </button>
                </div>
              ) : null}
            </>
          ) : originalError ? (
            <div class="source-viewer__empty">{originalError}</div>
          ) : (
            <div class="source-viewer__empty">Loading the original file…</div>
          )}
        </>
      ) : (
        <div class="source-viewer__note">
          No original file was retained for this source — only the reading below was captured.
        </div>
      )}

      {headedBlocks.length > 0 ? (
        <div class="srcnote" data-testid="source-note-blocks">
          {headedBlocks.map((block) => {
            // The unreadable block is the one that owes a person an ACTION, so
            // it is titled as one. Every other heading is the pack's own
            // sentence about the document and is rendered verbatim — this
            // screen does not rewrite a pack's words.
            const unreadable = block.heading === UNREADABLE_HEADING;
            return (
              <section
                key={block.heading ?? ''}
                class={unreadable ? 'srcnote__block srcnote__block--unread' : 'srcnote__block'}
              >
                <h3 class="srcnote__head">
                  {unreadable ? 'Couldn’t be read — check the original' : block.heading}
                </h3>
                <ul class="srcnote__lines">
                  {block.lines.map((line, i) => (
                    <li key={i} class="srcnote__line">
                      {line}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      ) : null}

      {rawText && (
        <details class="srcreading" open={!hasOriginal || isAudio}>
          <summary class="srcreading__summary">
            {isAudio ? 'Our reading (transcript)' : 'Our reading (text extraction)'}
          </summary>
          <div class="srcreading__caption">
            {/* CV5 §5, Huan's own words: THE RECORDING IS THE TRUTH, THE
                TRANSCRIPT IS OUR READING OF IT. So on an audio source the
                reading opens by default — it is what a person actually scans —
                while the sentence keeps saying which of the two is authority. */}
            {isAudio
              ? 'What we heard. The recording above is the source of truth.'
              : hasOriginal
                ? 'What we pulled out to fill the screens — the file above is the source of truth.'
                : 'What we pulled out to fill the screens.'}
          </div>
          <div class="srcdoc">{rawText}</div>
        </details>
      )}

      {!rawText && !hasOriginal && (
        <div class="source-viewer__empty">No text captured for this source.</div>
      )}
    </div>
  );
}
